// ============================================================
// No Internet Modal — Global Non-Dismissible Offline Overlay
// ============================================================
// Blocks interactions and protects against data loss when the user
// loses internet connectivity. Automatically dismisses when connection
// is restored.

import { useEffect } from 'react';
import { Dialog, DialogContent, Box, Typography, CircularProgress, keyframes } from '@mui/material';
import { WifiOff } from 'lucide-react';
import toast from 'react-hot-toast';
import { tokens } from '@/theme/tokens';
import useNetworkStore from '@/store/networkStore';

// Pulse animation for the offline warning beacon
const pulseGlow = keyframes`
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.5);
  }
  70% {
    transform: scale(1.05);
    box-shadow: 0 0 0 16px rgba(239, 68, 68, 0);
  }
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
  }
`;

const ripplePing = keyframes`
  0% {
    transform: scale(1);
    opacity: 0.8;
  }
  100% {
    transform: scale(1.6);
    opacity: 0;
  }
`;

export function NoInternetModal() {
  const isOffline = useNetworkStore((s) => s.isOffline);
  const setOffline = useNetworkStore((s) => s.setOffline);

  useEffect(() => {
    const handleOnline = () => {
      setOffline(false);
      toast.success('Internet connection restored!', {
        id: 'network-status-toast',
        duration: 3500,
      });
    };

    const handleOffline = () => {
      setOffline(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Periodic heartbeat to verify active network if marked offline
    let intervalId = null;
    if (isOffline) {
      intervalId = setInterval(async () => {
        if (navigator.onLine) {
          try {
            // Quick ping to check true connectivity (using favicon or lightweight endpoint)
            const res = await fetch('/favicon.ico', { method: 'HEAD', cache: 'no-store' });
            if (res.ok || res.status < 500) {
              handleOnline();
            }
          } catch {
            // Still offline
          }
        }
      }, 3500);
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (intervalId) clearInterval(intervalId);
    };
  }, [isOffline, setOffline]);

  return (
    <Dialog
      open={isOffline}
      onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return;
        }
      }}
      aria-labelledby="no-internet-title"
      aria-describedby="no-internet-description"
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(10px)',
            backgroundColor: 'rgba(15, 23, 42, 0.75)',
            zIndex: 99999,
          },
        },
        paper: {
          elevation: 24,
          sx: {
            borderRadius: '20px',
            p: { xs: 2.5, sm: 3.5 },
            maxWidth: 480,
            width: '92%',
            textAlign: 'center',
            backgroundColor: '#ffffff !important',
            color: '#0f172a !important',
            border: '2px solid #ef4444',
            boxShadow: '0 25px 60px -15px rgba(0, 0, 0, 0.6)',
            overflow: 'hidden',
            position: 'relative',
            zIndex: 100000,
          },
        },
      }}
    >
      <DialogContent sx={{ p: 0, overflow: 'visible' }}>
        {/* Animated Beacon Icon */}
        <Box sx={{ position: 'relative', width: 90, height: 90, mx: 'auto', mb: 3 }}>
          <Box
            sx={{
              position: 'absolute',
              inset: -8,
              borderRadius: '50%',
              backgroundColor: 'rgba(239, 68, 68, 0.25)',
              animation: `${ripplePing} 2s cubic-bezier(0, 0, 0.2, 1) infinite`,
            }}
          />
          <Box
            sx={{
              position: 'relative',
              width: 90,
              height: 90,
              borderRadius: '50%',
              backgroundColor: '#FEF2F2',
              border: '3px solid #ef4444',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#ef4444',
              boxShadow: '0 10px 25px -5px rgba(239, 68, 68, 0.4)',
              animation: `${pulseGlow} 2.5s infinite`,
            }}
          >
            <WifiOff size={44} strokeWidth={2.5} color="#ef4444" />
          </Box>
        </Box>

        {/* Header and Explanation */}
        <Typography
          id="no-internet-title"
          variant="h4"
          fontWeight={800}
          sx={{ color: '#0f172a !important', mb: 1.5, fontSize: { xs: '1.4rem', sm: '1.65rem' }, letterSpacing: '-0.02em' }}
        >
          No Internet Connection
        </Typography>

        <Typography
          id="no-internet-description"
          variant="body1"
          sx={{
            color: '#475569 !important',
            lineHeight: 1.6,
            mb: 3,
            fontSize: '0.95rem',
            px: { xs: 1, sm: 2 },
          }}
        >
          Your device appears to be offline. Please check your Wi-Fi or internet connection.
        </Typography>

        {/* Live Auto-reconnect status */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            pt: 2,
            borderTop: '1px solid #e2e8f0',
          }}
        >
          <CircularProgress size={18} thickness={5} sx={{ color: '#0284c7' }} />
          <Typography
            variant="body2"
            sx={{ color: '#64748b !important', fontWeight: 600 }}
          >
            Reconnecting automatically...
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default NoInternetModal;
